import React, { useState } from 'react'
import Modal from '../common/Modal'
import { useFileUpload } from '../../hooks/useFileUpload'
import { useMutation } from '@tanstack/react-query'
import { handlePutRequest } from '../../utils/api-handler.util'
import { showToaster } from '../../utils/show-toaster.util'
import { useAuth } from '../../contexts/AuthContext'
import { QueryKeys } from '../../utils/queryKeyGenerator'

const ImageUploadModal = ({ isOpen, onClose, currentImage, onImageUpdate }) => {
  const { currentUser } = useAuth()
  const [isUploading, setIsUploading] = useState(false)

  const { renderUploadInput, handleFileUpload, selectedFiles, clearFiles } = useFileUpload({
    label: 'Shop Picture',
    dropZoneOptions: {
      required: false,
      accept: {
        'image/*': ['.jpg', '.jpeg', '.png', '.webp'],
      },
      maxSize: 2 * 1024 * 1024, // 2MB
    },
  })

  const updateShopMutation = useMutation({
    mutationFn: async (shopPicture) => {
      return handlePutRequest(`/${currentUser.userType}s/my-shop`, { shopPicture })
    },
    onSuccess: () => {
      QueryKeys.myShopDetails.invalidate(currentUser.userType)
      showToaster('success', 'Shop picture updated successfully')
      onImageUpdate()
      onClose()
    },
    onError: () => {
      showToaster('error', 'Failed to update shop picture')
    },
  })

  const handleSave = async () => {
    if (selectedFiles.length === 0) return

    setIsUploading(true)
    try {
      const uploadedImageUrl = await handleFileUpload()
      if (uploadedImageUrl) {
        await updateShopMutation.mutateAsync(uploadedImageUrl)
      }
    } catch (error) {
      showToaster('error', 'Failed to upload image')
    } finally {
      setIsUploading(false)
    }
  }

  const handleClose = () => {
    clearFiles()
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="space-y-4">
        <h2 className="text-xl font-semibold text-gray-900">Update Shop Picture</h2>

        {currentImage && (
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-2">Current Picture:</p>
            <img
              src={currentImage}
              alt="Current shop"
              className="w-20 h-20 object-cover rounded-full mx-auto"
            />
          </div>
        )}

        <div>{renderUploadInput()}</div>
        <p className="text-sm text-foreground-muted">
          Image needs to be between 500x500 pixels and 1000x1000 pixels in JPG, PNG, or WEBP format.
          The maximum file size allowed is 2MB.
        </p>

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-neutral-200-100 border border-gray-300 rounded-md hover:bg-neutral-200"
            disabled={isUploading || updateShopMutation.isLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={selectedFiles.length === 0 || isUploading || updateShopMutation.isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-secondary-600 border border-transparent rounded-md hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUploading || updateShopMutation.isLoading ? 'Updating...' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default ImageUploadModal
